
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import { DomainRegistry } from "../../../../../domain";
import { NewObjectKeys} from "../../../../../domain/configuration";
import { IDrawableItem, AbstractVectorItem, AbstractPointsItem } from "../../../../../domain/document/items";
import { Point, Rectangle, Vector } from "../../../../../domain/drawing";
import { IDrawableItemEditable } from "../../IDrawableItemEditable";
import { CommonComponentsAccessibleMixin } from "../../mixins";

@Component
export default class NewObjectLocatableMixin extends Mixins(CommonComponentsAccessibleMixin) implements IDrawableItemEditable {
  @Prop()
  objectKey: NewObjectKeys;

  @Prop()
  isShown: boolean;

  data(): any {
    return {
      editingSource: null as IDrawableItem,
      process: null as () => void,
      isProcessing: false,
    };
  }
  editingSource: IDrawableItem;
  process: () => void;
  isProcessing: boolean;

  async createSource<T extends IDrawableItem = IDrawableItem>(complement?: (s: T) => void): Promise<T> {
    const source = await DomainRegistry.document.getDocumentRepository().createNewObjects(this.objectKey) as T;
    if (complement) {
      complement(source);
    }
    return source;
  }

  @Watch("editingSource")
  onNewObjectLocatorEditingSourceUpdated(newValue: IDrawableItem, oldValue: IDrawableItem): void {
    this.reprocess();
  }

  @Watch("process")
  onNewObjectLocatorProcessUpdated(newValue: () => void, oldValue: () => void): void {
    this.reprocess();
  }

  writeProperty(write: (source: any) => void): void {
    write(this.editingSource);
    this.reprocess();
  }

  reprocess(): void {
    if (this.isProcessing) {
      this.cancel();
    }
    if (this.process) {
      this.process();
    }
}

  @Watch("isShown")
  onNewObjectLocatorIsShownUpdated(newValue: boolean, oldValue: boolean) {
    if (newValue) {
      if (this.process) {
        this.process();
      }
    } else {
      this.cancel();
    }
  }

  @Emit()
  addItem(item: IDrawableItem): any {}

  emitAddItem(): void {
    this.addItem(this.editingSource.clone() as IDrawableItem);
  }

  locate(): void {
    this.processRequest(() => {
      this.commonComponents.editors.page.locateItem(this, this.editingSource)
        .then(l => {
          if (!l) {
            return;
          }
          this.editingSource.geometry.rectangle.topLeft = l;
          this.emitAddItem();
          this.isProcessing = false;
          this.locate();
        })
        .catch(e => this.$error(
          "オブジェクト追加失敗",
          "オブジェクト追加操作中にエラーが発生しました.",
          e
        ));
    });
  }

  locateVertically(): void {
    this.processRequest(() => {
      this.commonComponents.editors.page.locateItemVertically(this, this.editingSource)
        .then(l => {
          if (!l) {
            return;
          }
          const difference = l.subtract(this.editingSource.geometry.rectangle.topLeft);
          if (this.editingSource instanceof AbstractVectorItem) {
            this.editingSource.vector = this.editingSource.vector.move(difference);
          } else {
            this.editingSource.geometry.rectangle = this.editingSource.geometry.rectangle.move(difference);
          }
          this.emitAddItem();
          this.isProcessing = false;
          this.locateVertically();
        })
        .catch(e => this.$error(
          "オブジェクト追加失敗",
          "オブジェクト追加操作中にエラーが発生しました.",
          e
        ));
    });
  }

  layoutAspectRatioFixedRectangle(aspectRatio: number, minWidth: number = 0, minHeight: number = 0): void {
    this.processRequest(() => {
      this.commonComponents.editors.page.layoutRectangleItem(this, this.editingSource, aspectRatio)
        .then(r => {
          if (!r) {
            return;
          }
          if (r.size.width < minWidth) {
            r.size.width = minWidth;
            r.size.height = r.size.width / aspectRatio;
          }
          if (r.size.height < minHeight) {
            r.size.height = minHeight;
            r.size.width = r.size.height * aspectRatio;
          }
          this.editingSource.geometry.rectangle = r;
          this.emitAddItem();
          this.isProcessing = false;
          this.layoutAspectRatioFixedRectangle(aspectRatio, minWidth, minHeight);
        })
        .catch(e => this.$error(
          "オブジェクト追加失敗",
          "オブジェクト追加操作中にエラーが発生しました.",
          e
        ));
    });
  }

  layoutRectangle(minWidth: number = 0, minHeight: number = 0): void {
    this.processRequest(() => {
      this.commonComponents.editors.page.layoutRectangleItem(this, this.editingSource)
        .then(r => {
          if (!r) {
            return;
          }
          if (r.size.width < minWidth) { r.size.width = minWidth; }
          if (r.size.height < minHeight) { r.size.height = minHeight; }
          this.editingSource.geometry.rectangle = r;
          this.emitAddItem();
          this.isProcessing = false;
          this.layoutRectangle(minWidth, minHeight);
        })
        .catch(e => this.$error(
          "オブジェクト追加失敗",
          "オブジェクト追加操作中にエラーが発生しました.",
          e
        ));
    });
  }

  layoutLinear(): void {
    if (!(this.editingSource instanceof AbstractVectorItem)) {
      throw new Error("ソースがVectorItemではありません.実装を確認してください.");
    }
    this.processRequest(() => {
      this.commonComponents.editors.page.layoutLinearItem(this, this.editingSource)
      .then(v => {
        if (!v) {
          return;
        }
        if (this.shouldAddItem(v)) {
          (this.editingSource as AbstractVectorItem).vector = v;
          this.emitAddItem();
        }
        this.isProcessing = false;
        this.layoutLinear();
      })
      .catch(e => this.$error(
        "オブジェクト追加失敗",
        "オブジェクト追加操作中にエラーが発生しました.",
        e
      ));
    });
  }

  layoutPoints(): void {
    if (!(this.editingSource instanceof AbstractPointsItem)) {
      throw new Error("ソースがPointsItemではありません.実装を確認してください.");
    }
    this.processRequest(() => {
      this.commonComponents.editors.page.layoutPointsItem(this, this.editingSource)
      .then(p => {
        if (!p) {
          return;
        }
        if (this.shouldAddItem(p)) {
          (this.editingSource as AbstractPointsItem).points = p;
          this.emitAddItem();
        }
        this.isProcessing = false;
        this.layoutPoints();
      })
      .catch(e => this.$error(
        "オブジェクト追加失敗",
        "オブジェクト追加操作中にエラーが発生しました.",
        e
      ));
    });
  }

  private processRequest(callback: () => void): void {
    if (!this.isShown) {
      return; // 既に非表示になってる場合は処理しない.
    }
    if (!this.editingSource) {
      throw new Error("ソースが設定されていません.実装を確認してください.");
    }
    this.isProcessing = true;
    callback(); // tickで待つのはLocator側に任せる.
  }

  private shouldAddItem(parameter: Vector | Point[]): boolean {
    const configuration = this.$store.getters.configuration.behavior.document.editor.objectLocator.minSize;
    const rect = Rectangle.from(parameter);
    return (rect.width >= configuration.width || rect.height >= configuration.height);
  }

  cancel(): void {
    this.commonComponents.editors.page.cancel(this);
    this.isProcessing = false;
  }
}
